import { useEffect, useState, useCallback } from 'react';
import classNames from 'classnames';
import useResizeObserver from 'app/agriculture/hooks/useResizeObserver';
import { RichTextDisplay } from 'app/agriculture/components/RichTextDisplay/RichTextDisplay';

import './ExpandableTypography.scss';

const isTextClamped = ({ scrollHeight, clientHeight }: HTMLDivElement) => {
    return scrollHeight > clientHeight;
};

interface ExpandableTypographyProps {
    text: string;
    maxLines: number;
    id?: string;
    className?: string;
    disableExpandButton?: boolean;
}

export function ExpandableTypography({
    text,
    maxLines,
    id,
    className,
    disableExpandButton = false,
}: ExpandableTypographyProps) {
    const [expandable, setExpandable] = useState(false);
    const [expanded, setExpanded] = useState(false);

    const onResize = useCallback((target: HTMLDivElement) => {
        setExpandable(isTextClamped(target));
    }, []);

    const ref = useResizeObserver(onResize);

    const handleClick = useCallback(() => {
        setExpanded((expanded) => !expanded);
    }, []);

    useEffect(() => {
        if (!ref.current) return;
        onResize(ref.current);
    }, [ref, onResize]);

    if (!text) return null;

    return (
        <div
            className={classNames('ExpandableTypography', {
                'ExpandableTypography--expanded': expanded,
            })}
            ref={ref}
            style={
                !expanded
                    ? {
                          WebkitLineClamp: maxLines,
                          lineClamp: maxLines,
                      }
                    : {}
            }
        >
            <RichTextDisplay
                id={id}
                className={classNames('ExpandableTypography__text', className)}
                text={text}
            />
            {(expandable || expanded) && !disableExpandButton && (
                <button className="ExpandableTypography__button" onClick={handleClick}>
                    {!expanded ? 'Read more' : ' Read less'}
                </button>
            )}
        </div>
    );
}
